import {
  IgGreenPrimitives,
  IgIndigoPrimitives,
  IgSlatePrimitives,
} from "@intelligems/ig-design-tokens";
import {
  ToolResultOutput,
  UiMessageChunkDto,
  UiStreamAction,
} from "@intelligems/ig-types";

// TanStack DevTools' `enhancedLogs` rewrites direct `console.log(...)` calls at
// build time to prepend a source-link argument. That displaces the leading
// `%c`, breaking CSS-styled logs like the image preview below. Its AST matcher
// only targets literal `console.log`/`console.error` member calls, so an aliased
// reference renders the styled log untouched while enhanced logs stay on
// everywhere else.
const styledLog = console.log.bind(console);

const TOOL_LABEL_STYLE = `color: ${IgIndigoPrimitives[500]}; font-weight: bold; font-family: monospace`;
const OUTPUT_LABEL_STYLE = `color: ${IgGreenPrimitives[900]}; font-weight: bold; font-family: monospace`;
const META_STYLE = `color: ${IgSlatePrimitives[500]}; font-family: monospace`;

// Width-capped, aspect-preserved (no height squeeze) so the tool screenshot
// renders at the same wide size as the pristine reference logged by
// `logCaptureComparison` (plugin/embedded-preview) — the two sit side-by-side
// in the console for an easy A/B.
function renderImageInConsole(dataUrl: string, maxWidth = 480) {
  const img = new Image();
  img.onload = () => {
    const w = Math.min(img.naturalWidth, maxWidth);
    const h = Math.round(img.naturalHeight * (w / img.naturalWidth));
    styledLog(
      "%c ",
      `font-size:0;line-height:0;padding:${h / 2}px ${w / 2}px;` +
        `background:url(${dataUrl}) center/contain no-repeat;`
    );
  };
  img.onerror = () => {
    console.warn("[logToolActivity] failed to preview image");
  };
  img.src = dataUrl;
}

function logToolOutputMedia(output: ToolResultOutput) {
  if (output.type !== "content" || !Array.isArray(output.value)) return;
  for (const item of output.value) {
    if (
      item.type === "file-data" &&
      typeof item.mediaType === "string" &&
      item.mediaType.startsWith("image/") &&
      typeof item.data === "string"
    ) {
      renderImageInConsole(`data:${item.mediaType};base64,${item.data}`);
    }
  }
}

function logToolInput(
  part: Extract<UiMessageChunkDto, { type: "tool-input-available" }>
) {
  console.groupCollapsed(
    `%c🛠  ${part.toolName}%c  ${part.toolCallId.slice(0, 8)}`,
    TOOL_LABEL_STYLE,
    META_STYLE
  );
  console.log("input:", part.input);
  console.groupEnd();
}

function logToolOutput(
  part: Extract<UiMessageChunkDto, { type: "tool-output-available" }>
) {
  const output = part.output as ToolResultOutput;
  console.groupCollapsed(
    `%c←  output%c  ${part.toolCallId.slice(0, 8)}`,
    OUTPUT_LABEL_STYLE,
    META_STYLE
  );
  console.log("output:", output);
  logToolOutputMedia(output);
  console.groupEnd();
}

export function logToolActivity(action: UiStreamAction) {
  if (action.type !== "addStreamPart") return;
  const part = action.streamPart;
  if (part.type === "tool-input-available") {
    logToolInput(part);
  } else if (part.type === "tool-output-available") {
    logToolOutput(part);
  }
}
